import { detectCategory, getDisplayUnit } from './unitConvert.js';

const SOURCE_PREFIXES = new Set(['ecu', 'obd', 'obdii', 'can', 'gps', 'aim', 'logger']);
const NON_VALUE_QUALIFIERS = new Set([
  'active', 'current', 'high', 'light', 'limit', 'limiter', 'low', 'max', 'min',
  'pressed', 'rate', 'ratio', 'state', 'status', 'sts', 'switch', 'sw', 'target',
  'warn', 'warning',
]);

function normalizedUnit(unit) {
  return String(unit || '').toLowerCase().trim().replace(/\s+/g, '').replace(/°/g, 'deg');
}

function unitKey(unit) {
  if (!String(unit || '').trim()) return null;
  const category = detectCategory(unit);
  if (category) return `category:${category}`;
  const value = normalizedUnit(unit);
  if (new Set(['%', 'pct', 'percent', 'percentage']).has(value)) return 'unit:percent';
  if (new Set(['rpm', 'rev/min', 'revs/min', '1/min']).has(value)) return 'unit:rpm';
  if (new Set(['deg', 'degree', 'degrees']).has(value)) return 'unit:deg';
  if (new Set(['deg/s', 'degs', 'degree/s', 'degrees/s']).has(value)) return 'unit:deg/s';
  if (new Set(['g', 'gee']).has(value)) return 'unit:g';
  if (new Set(['v', 'volt', 'volts']).has(value)) return 'unit:v';
  if (new Set(['a', 'amp', 'amps']).has(value)) return 'unit:a';
  return `unit:${value}`;
}

function nameTokens(name) {
  const tokens = String(name || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  while (tokens.length && (SOURCE_PREFIXES.has(tokens[0]) || /^uc\d+$/.test(tokens[0]))) {
    tokens.shift();
  }
  return tokens;
}

function nameFamily(name) {
  const tokens = nameTokens(name);
  if (!tokens.length || tokens.some(token => NON_VALUE_QUALIFIERS.has(token))) return null;
  const compact = tokens.join('');
  if (/(?:throttle|throtle|etps|tps|pps|acceleratorpedal|accped)/.test(compact)) return 'throttle';
  if (compact.includes('brake') && /(?:press|pressure|psi|pedal|pos)/.test(compact)) return 'brake';
  if (compact === 'rpm' || compact === 'enginerpm' || compact === 'enginespeed') return 'rpm';
  if (compact.includes('steer') && /(?:angle|pos)/.test(compact)) return 'steering';
  if (compact.includes('speed')) return 'speed';
  if (compact.includes('yaw') && /(?:rate|gyro)/.test(compact)) return 'yaw_rate';
  return compact;
}

function editDistance(a, b) {
  const prev = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const next = [i];
    for (let j = 1; j <= b.length; j += 1) {
      next[j] = Math.min(
        next[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    for (let j = 0; j < next.length; j += 1) prev[j] = next[j];
  }
  return prev[b.length];
}

function namesAreCompatible(names) {
  const families = names.map(nameFamily);
  if (families.some(family => !family)) return false;
  if (families.every(family => family === families[0])) return true;
  const base = families[0];
  return families.every(family => {
    const longest = Math.max(base.length, family.length);
    return longest >= 6 && editDistance(base, family) / longest <= 0.15;
  });
}

function quantile(sorted, fraction) {
  const index = (sorted.length - 1) * fraction;
  const lo = Math.floor(index);
  const hi = Math.ceil(index);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (index - lo);
}

function robustRange(values) {
  const finite = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (finite.length < 10) return null;
  const low = quantile(finite, 0.05);
  const high = quantile(finite, 0.95);
  const span = high - low;
  if (!(span > Math.max(1e-9, Math.max(Math.abs(low), Math.abs(high)) * 1e-6))) return null;
  return { low, high, span };
}

function rangesAreCompatible(ranges) {
  const reference = ranges[0];
  return ranges.slice(1).every(candidate => {
    const spanRatio = Math.max(reference.span, candidate.span)
      / Math.min(reference.span, candidate.span);
    if (spanRatio > 4) return false;
    const pad = Math.max(reference.span, candidate.span) * 0.1;
    return reference.high + pad >= candidate.low && candidate.high + pad >= reference.low;
  });
}

function unitsFor(signal) {
  const units = Array.isArray(signal.units) && signal.units.length
    ? signal.units
    : [signal.unit || ''];
  return units;
}

/**
 * Decide whether several user-selected signals can honestly share one numeric
 * axis. Known units are authoritative. Missing units may be inferred only when
 * channel names and already-displayed value ranges independently agree; this
 * function never applies an inferred multiplier or offset.
 */
export function assessSharedScale(signals, unitPrefs = {}) {
  if (!Array.isArray(signals) || signals.length < 2) {
    return { compatible: false, inferred: false, pending: false, reason: 'Select another channel first.', displayUnit: '' };
  }

  const allUnits = signals.flatMap(unitsFor);
  const knownUnits = allUnits.filter(unit => String(unit || '').trim());
  const keys = new Set(knownUnits.map(unitKey));
  if (keys.size > 1) {
    return { compatible: false, inferred: false, pending: false, reason: 'Selected channels use incompatible units.', displayUnit: '' };
  }

  const displayUnit = knownUnits.length ? getDisplayUnit(knownUnits[0], unitPrefs) : '';
  const hasMissing = knownUnits.length !== allUnits.length;
  if (!hasMissing) {
    return { compatible: true, inferred: false, pending: false, reason: '', displayUnit };
  }

  if (!namesAreCompatible(signals.map(signal => signal.name))) {
    return { compatible: false, inferred: false, pending: false, reason: 'Missing units cannot be inferred for these channel names.', displayUnit };
  }

  const ranges = signals.map(signal => robustRange(signal.values || []));
  if (ranges.some(range => !range)) {
    return { compatible: false, inferred: true, pending: true, reason: 'Waiting for enough varying channel data to infer scale compatibility.', displayUnit };
  }
  if (!rangesAreCompatible(ranges)) {
    return { compatible: false, inferred: true, pending: false, reason: 'Observed channel ranges use different numeric scales.', displayUnit };
  }
  return {
    compatible: true,
    inferred: true,
    pending: false,
    reason: 'Compatibility inferred from channel names and observed ranges.',
    displayUnit,
  };
}

export function domainForSeries(series) {
  let low = Infinity;
  let high = -Infinity;
  for (const item of series || []) {
    for (const point of item.points || []) {
      if (!Number.isFinite(point.value)) continue;
      low = Math.min(low, point.value);
      high = Math.max(high, point.value);
    }
  }
  if (!Number.isFinite(low) || !Number.isFinite(high)) return [0, 100];
  if (low === high) {
    const pad = Math.abs(low) * 0.1 || 1;
    return [low - pad, high + pad];
  }
  const pad = (high - low) * 0.05;
  return [low - pad, high + pad];
}

// "Nice" round tick values across [min, max], always including 0 when straddled.
export function niceTicks(min, max, count = 6) {
  const span = max - min;
  if (!(span > 0)) return [min, max];
  const rawStep = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const ticks = [];
  for (let t = Math.ceil(min / step) * step; t <= max + step * 1e-6; t += step) ticks.push(t);
  if (min < 0 && max > 0 && !ticks.some(t => Math.abs(t) < step * 1e-6)) ticks.push(0);
  return ticks.sort((a, b) => a - b);
}
